import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import { BrowseEventTrackingProvider } from '@udemy-v2/react-browse-event-tracking';
import { CoursePriceProvider } from '@udemy-v2/react-course-pricing';
import { RecommendationSourceObjectType, RecommendationsProvider } from '@udemy-v2/react-recommendations';

// Maximum number of courses requested per recommendation unit returned for the
// limited-access surface.
const RECOMMENDATIONS_ITEM_COUNT_PER_UNIT = 12;
export type LimitedAccessRecommendationsContextId = 'disabled_course_label' | 'disabled_course_cat';
export interface LimitedAccessRecommendationsSourceObject {
  id: string;
  type: RecommendationSourceObjectType;
}
export interface LimitedAccessRecommendationsWrapperProps {
  /**
   * Seed objects forwarded to the recommendations service. The accepted
   * `RecommendationSourceObjectType` varies based on the upstream surface:
   *  - topic            → RecommendationSourceObjectType.Label
   *  - category         → RecommendationSourceObjectType.Category
   *  - subcategory      → RecommendationSourceObjectType.Subcategory
   */
  sourceObjects: LimitedAccessRecommendationsSourceObject[];
  /** Recommendation context code, decided by the calling surface. */
  contextId: LimitedAccessRecommendationsContextId;
  children: React.ReactNode;
}

// relatedSourceType values must match the @options in the DiscoveryItemImpressionEvent Avro
// schema — these are short taxonomy names, not the course_* prefixed DiscoveryItemTypeOptions.
const SOURCE_TYPE_TO_RELATED_SOURCE_TYPE: Partial<Record<RecommendationSourceObjectType, string>> = {
  [RecommendationSourceObjectType.Label]: 'topic',
  [RecommendationSourceObjectType.Category]: 'category',
  [RecommendationSourceObjectType.Subcategory]: 'subcategory'
};

/**
 * Wraps the children with the RecommendationsProvider + BrowseEventTrackingProvider
 * used by the limited-access course screens (UnavailableCourse,
 * OrganizationCourseNotAvailable). Renders null when no seed objects are
 * supplied — without a seed there is nothing meaningful to recommend.
 */
export const LimitedAccessRecommendationsWrapper = t0 => {
  const $ = _c(16);
  const {
    sourceObjects,
    contextId,
    children
  } = t0;
  let t1;
  if ($[0] !== contextId || $[1] !== sourceObjects) {
    t1 = {
      contextId,
      sourceObjects,
      requestedItemCountPerUnit: RECOMMENDATIONS_ITEM_COUNT_PER_UNIT
    };
    $[0] = contextId;
    $[1] = sourceObjects;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const contextInput = t1;
  let t2;
  bb0: {
    const first = sourceObjects[0];
    if (!first) {
      t2 = undefined;
      break bb0;
    }
    const relatedSourceType = SOURCE_TYPE_TO_RELATED_SOURCE_TYPE[first.type];
    if (!relatedSourceType) {
      t2 = undefined;
      break bb0;
    }
    let t3;
    if ($[3] !== first.id || $[4] !== relatedSourceType) {
      t3 = {
        relatedSourceId: first.id,
        relatedSourceType
      };
      $[3] = first.id;
      $[4] = relatedSourceType;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const trackingContext = t2;
  if (sourceObjects.length === 0) {
    return null;
  }
  let t3;
  if ($[6] !== trackingContext) {
    t3 = trackingContext ?? {};
    $[6] = trackingContext;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children) {
    t4 = <CoursePriceProvider>{children}</CoursePriceProvider>;
    $[8] = children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3 || $[11] !== t4) {
    t5 = <BrowseEventTrackingProvider browseEventTrackingContext={t3}>{t4}</BrowseEventTrackingProvider>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== contextInput || $[14] !== t5) {
    t6 = <RecommendationsProvider contextInput={contextInput} includeAvailableSubscriptionInfo={true}>{t5}</RecommendationsProvider>;
    $[13] = contextInput;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};